import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  Grid,
  Chip,
  Alert,
  MenuItem,
  IconButton,
  Menu,
  Stack,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import apiClient from "api/apiClient";
import PricingPage from "../plan";
import useMessageToast from "hooks/useMessageToast";

const BillingTab = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  useMessageToast(error, "error");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPmId, setMenuPmId] = useState(null);

  const handleMenuOpen = (event, pmId) => {
    setAnchorEl(event.currentTarget);
    setMenuPmId(pmId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPmId(null);
  };

  const fetchMethods = async () => {
    try {
      const res = await apiClient.get(
        `${process.env.REACT_APP_BASE_URL}/payments/payment-methods`,
      );
      setMethods(res.data);
    } catch (err) {
      console.error("Failed to load payment methods:", err);
      setError("Unable to fetch payment methods.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setAdding(true);
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/create-setup-session`,
        {},
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Failed to start setup session:", err);
      setError("Unable to add a new payment method.");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (pmId) => {
    if (methods.length <= 1) {
      alert("You must have at least one payment method.");
      return;
    }
    try {
      await apiClient.delete(
        `${process.env.REACT_APP_BASE_URL}/payments/delete-payment-method`,
        {
          data: { paymentMethodId: pmId }, // Must include 'data' key for DELETE
        },
      );
      await fetchMethods();
    } catch (err) {
      console.error("Delete failed", err);
      setError("Failed to delete payment method.");
    }
  };

  const handleSetDefault = async (pmId) => {
    try {
      await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/payments/set-default-payment`,
        { paymentMethodId: pmId },
      );
      await fetchMethods();
    } catch (err) {
      console.error("Failed to set default", err);
      setError("Could not update default payment method.");
    }
  };

  useEffect(() => {
    fetchMethods();
  }, []);

  return (
    <Box className="settings-section">
      <Stack spacing={4}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Personal Workspace Billing
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Manage your personal subscription plan, simulation credits, and saved
            payment methods in one place.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Payment Methods
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saved cards here are used for future personal-workspace charges.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <CircularProgress />
          ) : (
            <Grid container direction="column" spacing={2}>
              {methods.map((pm) => (
                <Grid item xs={12} key={pm.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: 2,
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {pm.brand.toUpperCase()} ****{pm.last4}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        Exp {pm.exp_month}/{pm.exp_year}
                      </Typography>
                      {pm.isDefault && (
                        <Chip
                          label="Default"
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>

                    <Box>
                      {!pm.isDefault && (
                        <>
                          <IconButton onClick={(e) => handleMenuOpen(e, pm.id)}>
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={menuPmId === pm.id}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <MenuItem
                              onClick={() => {
                                handleSetDefault(pm.id);
                                handleMenuClose();
                              }}
                            >
                              Set as Default
                            </MenuItem>
                            {methods.length > 1 && (
                              <MenuItem
                                onClick={() => {
                                  handleDelete(pm.id);
                                  handleMenuClose();
                                }}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </Menu>
                        </>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              onClick={handleAddPaymentMethod}
              disabled={adding}
            >
              {adding ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Add New Payment Method"
              )}
            </Button>
          </Box>
        </Box>

        <Box>
          <PricingPage embedded />
        </Box>
      </Stack>
    </Box>
  );
};

export default BillingTab;
